import React, { useState, useEffect, useLayoutEffect } from "react";
import { Link, Outlet, useLocation, useParams, useNavigate } from "react-router-dom";
import { FileText, Lock, Phone } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { useIsMobile } from "../../hooks/use-mobile";
import { useReportIds } from "../../hooks/useReportIds";
import { useCallsByType } from "../../hooks/useCallsByType";
import Loading from "../Loading";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Checkbox } from "../ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import Toast from '../Toast.jsx';
import { useTranslation } from "react-i18next";
import { useUser } from "@clerk/clerk-react";
import { format } from "date-fns";
import { SchedulePill } from "../SchedulePill.js";

const CompletedSessionButton = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation();
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-block">
            <Button 
              onClick={() => {}}
              variant="outline" 
              className="flex items-center text-sm md:text-base bg-gray-200 hover:bg-gray-200 text-gray-500 cursor-not-allowed opacity-80 transition-all" 
              disabled
            >
              {children}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent className="bg-gray-800 text-white border-gray-800">
          <p>{t("intakeProcess.sessionStatus.completed")}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const LockedSessionButton = ({ stage, children }: { stage: number, children: React.ReactNode }) => {
  const { t } = useTranslation();
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-block">
            <Button 
              variant="outline" 
              className="flex items-center text-sm md:text-base bg-gray-200 hover:bg-gray-200 text-gray-500 cursor-not-allowed opacity-80 transition-all" 
              disabled
            >
              {children}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent className="bg-gray-800 text-white border-gray-800">
          <p>{t("intakeProcess.sessionStatus.locked", { stage })}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const IntakeProcess = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useUser();
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [microphoneChecked, setMicrophoneChecked] = useState(false);
  const [medicalChecked, setMedicalChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const { reportId } = useParams();
  const { reportIds, reportDates, isLoading } = useReportIds();
  const { calls: callsByType, loading: callsLoading, refetch: refetchCalls } = useCallsByType();
  
  const [sessionStatus, setSessionStatus] = useState({
    session1: { completed: false, scheduled: false, 
      date: "Not scheduled" 
    },
    session2: { completed: false, scheduled: false, 
      date: "Not scheduled" 
    },
    session3: { completed: false, scheduled: false, 
      date: "Not scheduled" 
    },
  });

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState({
    message: '',
    variant: 'success',
  });

  const location = useLocation(); // Get current location
  
  // Define isBaseIntakePath here before any conditional returns
  const isBaseIntakePath = location.pathname === '/auth/start-report' || location.pathname === '/auth/start-report/' ||
                           (location.pathname.startsWith('/auth/report/') && location.pathname.split('/').length === 4);
                           
  // Define allChecked here
  const allChecked = privacyChecked && microphoneChecked && medicalChecked;
  
  // Define effectiveReportId here
  const effectiveReportId = reportId || reportIds.INTAKE;

  // Handle routing with useEffect instead of conditional returns
  useEffect(() => {
    if (isLoading) return; // Don't navigate while loading

    // Get the current path
    const currentPath = location.pathname;
    
    // Skip navigation logic if we're viewing any specific report pages
    if (currentPath.includes('/intake1') || 
        currentPath.includes('/intake2') || 
        currentPath.includes('/intake3') ||
        currentPath.includes('/intake')) {
      return;
    }

    // If all intake reports are completed, navigate to the final report
    if (reportIds.INTAKE1 && reportIds.INTAKE2 && reportIds.INTAKE3 && reportIds.INTAKE) {
      // Only redirect if we're not already on the final report page
      const targetPath = `/auth/report/${reportIds.INTAKE}`;
      if (!currentPath.includes(targetPath)) {
        navigate(targetPath, {
          state: { preserveState: true }
        });
        return;
      }
    }
    
    // If there's no intake ID and we're not on the start-report page, redirect to start-report
    if (!reportIds.INTAKE && !currentPath.includes('/auth/start-report')) {
      navigate('/auth/start-report', {
        state: { preserveState: true }
      });
      return;
    }
  }, [reportIds, isLoading, location.pathname, navigate]);

  // Fetch session status when report IDs or calls are available
  useEffect(() => {
    if ((!reportIds.INTAKE1 && !reportIds.INTAKE2 && !reportIds.INTAKE3) && 
        (!callsByType.INTAKE1 && !callsByType.INTAKE2 && !callsByType.INTAKE3)) return;
    
    const fetchSessionStatus = async () => {
      try {
        const currentDate = new Date();
        
        // Helper function to check if session is completed based on report date
        const isSessionCompleted = (reportId: string | undefined, reportDate: string | undefined) => {
          if (!reportId || !reportDate) return false;
          const reportCreatedAt = new Date(reportDate);
          return reportCreatedAt < currentDate;
        };
        
        // Helper function to format date for display
        const formatDateForDisplay = (dateString: string) => {
          try {
            return format(new Date(dateString), 'MMM d, yyyy');
          } catch {
            return "Invalid date";
          }
        };
        
        // Helper function to get call date for display
        const getCallDate = (callType: 'INTAKE1' | 'INTAKE2' | 'INTAKE3') => {
          const call = callsByType[callType];
          if (call && call.scheduledAt) {
            return formatDateForDisplay(call.scheduledAt);
          }
          return "Not scheduled";
        };
        
        setSessionStatus({
          session1: { 
            completed: isSessionCompleted(reportIds.INTAKE1, reportDates?.INTAKE1), 
            scheduled: !!(callsByType.INTAKE1 && callsByType.INTAKE1.status !== 'COMPLETED'), 
            date: reportIds.INTAKE1 && reportDates?.INTAKE1 
              ? formatDateForDisplay(reportDates.INTAKE1)
              : getCallDate('INTAKE1')
          },
          session2: { 
            completed: isSessionCompleted(reportIds.INTAKE2, reportDates?.INTAKE2), 
            scheduled: !!(callsByType.INTAKE1 && callsByType.INTAKE1.status === 'COMPLETED' && callsByType.INTAKE2 && callsByType.INTAKE2.status !== 'COMPLETED'), 
            date: reportIds.INTAKE2 && reportDates?.INTAKE2 
              ? formatDateForDisplay(reportDates.INTAKE2)
              : getCallDate('INTAKE2')
          },
          session3: { 
            completed: isSessionCompleted(reportIds.INTAKE3, reportDates?.INTAKE3), 
            scheduled: !!((callsByType.INTAKE2 && callsByType.INTAKE1 && callsByType.INTAKE2.status === 'COMPLETED' && callsByType.INTAKE1.status === 'COMPLETED')), 
            date: reportIds.INTAKE3 && reportDates?.INTAKE3 
              ? formatDateForDisplay(reportDates.INTAKE3)
              : getCallDate('INTAKE3')
          },
        });
      } catch (error) {
        console.error("Error fetching session status:", error);
      }
    };
    
    fetchSessionStatus();
  }, [JSON.stringify(reportIds), JSON.stringify(reportDates), JSON.stringify(callsByType)]);

  // Add this useLayoutEffect to handle hash links when the page loads
  useLayoutEffect(() => {
    if (location.hash && isBaseIntakePath) {
      const id = location.hash.substring(1);
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location.hash, isBaseIntakePath]);

  const handleStartCall = async (intakeNumber = 1) => {
    setLoading(true);
    try {
      const startResponse = await fetch(`${(import.meta as any).env.VITE_APP_URL}/intake_call/${intakeNumber}`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uid: user?.id,
        }),
      });
      
      if (!startResponse.ok) {
        throw new Error('Failed to start call');
      }
      
      const responseData = await startResponse.json();
      
      if (responseData?.status === 'success') {
        setToastMessage({
          message: 'common.toast.callStarted',
          variant: 'success',
        });
        setShowToast(true);
        
        // Pass the entire response data as sessionData to the StartSession component
        navigate(`${reportIds.INTAKE ? '/auth/start-session' : '/start-session'}`, { 
          state: { 
            sessionData: responseData,
            preserveState: true
          } 
        });
      }
    } catch (error) {
      console.error('Error starting call:', error);
      setToastMessage({
        message: 'common.toast.callFailed',
        variant: 'error',
      });
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  const handleReschedule = async (sessionKey: string, date: string, time: string) => {    
    // Refetch the data to update the session status
    try {
      // Refetch both reports and calls to update the session status
      // The sessionStatus will be updated through the useEffect that depends on both reportIds and callsByType
      refetchCalls();
    } catch (error) {
      console.error('Error refetching data:', error);
    }
  };

  if (isLoading || callsLoading) {
    return <Loading className="h-screen" />;
  }
  console.log(sessionStatus);
  return (
    <div className="bg-gray-50">
      <div className={`${!isLoading ? 'container max-w-4xl px-4 py-8 md:py-12' : ''} min-h-screen`}>
        {/* Only show title/intro on base intake pages, not on specific reports */}
        {isBaseIntakePath && (
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-2 text-gray-900">{t("intakeProcess.title")}</h1>
            <p className="text-base md:text-lg text-gray-600">
              {t("intakeProcess.subtitle")}
            </p>
            {reportIds?.INTAKE && (
              <div className="mt-6">
                <Link to={`/auth/report/${effectiveReportId}/intake`}>
                <Button className="bg-primary hover:bg-primary/90 w-full sm:w-auto">
                <FileText className="mr-2 h-5 w-5" />
                {t("intakeProcess.viewFinalReport")}
              </Button>
                </Link>
              </div>
            )}
          </div>
        )}

        {/* Render the child route component (e.g., InitialAssessmentReport) */}
        <Outlet /> 

        {/* Only show the session cards on the base intake page */}
        {isBaseIntakePath && (
          <div className="space-y-6">
            {/* Initial Health Assessment Card */}
            <Card id="initial-assessment" className="border border-gray-200 shadow-sm">
              <CardContent className={`${isMobile ? 'p-4' : 'p-6'}`}>
                <div className="flex justify-between items-start mb-4 flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white font-medium">
                      1
                    </div>
                    <h2 className="text-lg md:text-xl font-semibold">{t("intakeProcess.session.initialAssessment.title")}</h2>
                  </div>
                  <SchedulePill
                    status={sessionStatus.session1.completed ? 'completed' : (sessionStatus.session1.scheduled ? 'scheduled' : 'not-scheduled')}
                    appointment={callsByType.INTAKE1 || (reportIds.INTAKE1 ? {
                      id: reportIds.INTAKE1,
                      scheduledAt: reportDates?.INTAKE1 || new Date().toISOString(),
                      status: 'COMPLETED' as const,
                      type: 'INTAKE1' as const,
                      reportId: reportIds.INTAKE1,
                      createdAt: reportDates?.INTAKE1
                    } : {
                      type: 'INTAKE1' as const,
                      status: 'PENDING' as const
                    })}
                    date={sessionStatus.session1.date}
                    time={sessionStatus.session1.date ? sessionStatus.session1.date.split(' ')[1] + ' ' + sessionStatus.session1.date.split(' ')[2] : ''}
                    completedDate={sessionStatus.session1.completed ? sessionStatus.session1.date : undefined}
                    onReschedule={(date, time) => handleReschedule('session1', date, time)}
                    onSuccess={(updatedAppointment) => {
                      handleReschedule('session1', '', '');
                      refetchCalls();
                    }}
                    reportDate={reportIds.INTAKE1 && reportDates?.INTAKE1 ? format(new Date(reportDates.INTAKE1), 'MMM d, yyyy') : undefined}
                    callDate={callsByType.INTAKE1 && callsByType.INTAKE1.scheduledAt ? format(new Date(callsByType.INTAKE1.scheduledAt), 'MMM d, yyyy') : undefined}
                    hasReport={!!reportIds.INTAKE1}
                  />
                </div>

                <p className="text-sm md:text-base text-gray-700 mb-6 text-left">
                  {t("intakeProcess.session.initialAssessment.description")}
                </p>

                <div className="mb-6 text-left">
                  <h3 className="font-medium text-gray-900 mb-2">{t("intakeProcess.session.initialAssessment.focusAreas")}</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-600 pl-2 text-sm md:text-base">
                    <li>{t("intakeProcess.session.initialAssessment.areas.healthHistory")}</li>
                    <li>{t("intakeProcess.session.initialAssessment.areas.currentLifestyle")}</li>
                    <li>{t("intakeProcess.session.initialAssessment.areas.substanceUse")}</li>
                    <li>{t("intakeProcess.session.initialAssessment.areas.goalExploration")}</li>
                  </ul>
                </div>

                <div className="flex gap-2 md:gap-4 flex-wrap justify-center">
                  {sessionStatus.session1.completed ? (
                    <CompletedSessionButton>
                      <Phone className="mr-2 h-4 w-4" />
                      {t("intakeProcess.buttons.talkToHana")}
                    </CompletedSessionButton>
                  ) : (
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="flex items-center text-sm md:text-base bg-primary text-white hover:bg-primary/90">
                          <Phone className="mr-2 h-4 w-4" />
                          {t("intakeProcess.buttons.talkToHana")}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                          <DialogTitle className="text-xl">{t("intakeProcess.session.initialAssessment.dialogTitle")}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <p className="text-sm text-gray-600">{t("intakeProcess.session.initialAssessment.dialogDescription")}</p>
                          
                          <div className="bg-gray-50 p-4 rounded-md">
                            <h3 className="font-medium text-gray-900 mb-2">{t("intakeProcess.session.initialAssessment.focusAreas")}</h3>
                            <ul className="list-disc list-inside space-y-1 text-gray-600 pl-2 text-sm">
                              <li>{t("intakeProcess.session.initialAssessment.areas.healthHistory")}</li>
                              <li>{t("intakeProcess.session.initialAssessment.areas.currentLifestyle")}</li>
                              <li>{t("intakeProcess.session.initialAssessment.areas.substanceUse")}</li>
                              <li>{t("intakeProcess.session.initialAssessment.areas.goalExploration")}</li>
                            </ul>
                          </div>

                          <div className="space-y-4 pt-2">
                            <div className="space-y-2">
                              <div className="flex items-center space-x-2">
                                <Checkbox 
                                  id="privacy" 
                                  checked={privacyChecked}
                                  onCheckedChange={(checked) => setPrivacyChecked(checked === true)}
                                />
                                <label
                                  htmlFor="privacy"
                                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                >
                                  {t("intakeProcess.consent.privacy.label")} <span className="text-red-600">*</span>
                                </label>
                              </div>
                              <p className="text-xs text-gray-500 ml-6">
                                {t("intakeProcess.consent.privacy.description")}
                              </p>
                            </div>

                            <div className="space-y-2">
                              <div className="flex items-center space-x-2">
                                <Checkbox
                                  id="microphone"
                                  checked={microphoneChecked}
                                  onCheckedChange={(checked) => setMicrophoneChecked(checked === true)}
                                />
                                <label
                                  htmlFor="microphone"
                                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                >
                                  {t("intakeProcess.consent.microphone.label")} <span className="text-red-600">*</span>
                                </label>
                              </div>
                              <p className="text-xs text-gray-500 ml-6">
                                {t("intakeProcess.consent.microphone.description")}
                              </p>
                            </div>

                            <div className="space-y-2">
                              <div className="flex items-center space-x-2">
                                <Checkbox
                                  id="medical"
                                  checked={medicalChecked}
                                  onCheckedChange={(checked) => setMedicalChecked(checked === true)}
                                />
                                <label
                                  htmlFor="medical"
                                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                >
                                  {t("intakeProcess.consent.medical.label")} <span className="text-red-600">*</span>
                                </label>
                              </div>
                              <p className="text-xs text-gray-500 ml-6">
                                {t("intakeProcess.consent.medical.description")}
                              </p>
                            </div>
                          </div>

                          <div className="flex justify-center pt-4">
                            <Button 
                              onClick={() => handleStartCall(1)} 
                              disabled={!allChecked || loading}
                              className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white"
                            >
                              {loading ? t("intakeProcess.buttons.connecting") : t("intakeProcess.buttons.startSession")}
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  )}
                  
                  {reportIds.INTAKE1 ? (
                    <Link to={reportIds.INTAKE1 && reportIds.INTAKE ? `/auth/report/${reportIds.INTAKE1}/intake1` : `/auth/start-report/intake1`}>
                      <Button variant="outline" className="flex items-center text-sm md:text-base bg-[#EBF4F0] border-[#EBF4F0] hover:bg-[#EBF4F0]/90 hover:border-[#EBF4F0]/90 text-gray-700">
                        <FileText className="mr-2 h-4 w-4" />
                        {t("intakeProcess.buttons.viewReport")}
                      </Button>
                    </Link>
                  ) : reportIds.INTAKE2 ? (
                    <Link to={reportIds.INTAKE2 && reportIds.INTAKE  ? `/auth/report/${reportIds.INTAKE2}/intake2` : `/auth/start-report/intake2`}>
                      <Button variant="outline" className="flex items-center text-sm md:text-base bg-[#EBF4F0] border-[#EBF4F0] hover:bg-[#EBF4F0]/90 hover:border-[#EBF4F0]/90 text-gray-700">
                        <FileText className="mr-2 h-4 w-4" />
                        {t("intakeProcess.buttons.viewReport")}
                      </Button>
                    </Link>
                  ) : reportIds.INTAKE3 ? (
                    <Link to={reportIds.INTAKE3 && reportIds.INTAKE ? `/auth/report/${reportIds.INTAKE3}/intake3` : `/auth/start-report/intake3`}>
                      <Button variant="outline" className="flex items-center text-sm md:text-base bg-[#EBF4F0] border-[#EBF4F0] hover:bg-[#EBF4F0]/90 hover:border-[#EBF4F0]/90 text-gray-700">
                        <FileText className="mr-2 h-4 w-4" />
                        {t("intakeProcess.buttons.viewReport")}
                      </Button>
                    </Link>
                  ) : (
                    <Button variant="outline" className="flex items-center text-sm md:text-base bg-gray-100 text-gray-500 cursor-not-allowed" disabled>
                      <FileText className="mr-2 h-4 w-4" />
                      {t("intakeProcess.buttons.reportUnavailable")}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Dividing Line */}
            <div className="w-1 h-12 bg-gray-200 mx-auto"></div>

            {/* Health Assessment Session Card */}
            <Card id="health-assessment" className={`border border-gray-200 shadow-sm ${!sessionStatus.session1.completed ? 'opacity-70' : ''}`}>
              <CardContent className={`${isMobile ? 'p-4' : 'p-6'}`}>
                <div className="flex justify-between items-start mb-4 flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white font-medium">
                      2
                    </div>
                    <h2 className="text-lg md:text-xl font-semibold">{t("intakeProcess.session.wellnessPlanning.title")}</h2>
                  </div>
                  <SchedulePill
                    status={sessionStatus.session2.completed ? 'completed' : (sessionStatus.session2.scheduled ? 'scheduled' : 'not-scheduled')}
                    appointment={callsByType.INTAKE2 || (reportIds.INTAKE2 ? {
                      id: reportIds.INTAKE2,
                      scheduledAt: reportDates?.INTAKE2 || new Date().toISOString(),
                      status: 'COMPLETED' as const,
                      type: 'INTAKE2' as const,
                      reportId: reportIds.INTAKE2,
                      createdAt: reportDates?.INTAKE2
                    } : {
                      type: 'INTAKE2' as const,
                      status: 'PENDING' as const
                    })}
                    date={sessionStatus.session2.date}
                    time={sessionStatus.session2.date ? sessionStatus.session2.date.split(' ')[1] + ' ' + sessionStatus.session2.date.split(' ')[2] : ''}
                    completedDate={sessionStatus.session2.completed ? sessionStatus.session2.date : undefined}
                    onReschedule={(date, time) => handleReschedule('session2', date, time)}
                    onSuccess={(updatedAppointment) => {
                      handleReschedule('session2', '', '');
                      refetchCalls();
                    }}
                    reportDate={reportIds.INTAKE2 && reportDates?.INTAKE2 ? format(new Date(reportDates.INTAKE2), 'MMM d, yyyy') : undefined}
                    callDate={callsByType.INTAKE2 && callsByType.INTAKE2.scheduledAt ? format(new Date(callsByType.INTAKE2.scheduledAt), 'MMM d, yyyy') : undefined}
                    hasReport={!!reportIds.INTAKE2}
                  />
                </div>

                <p className="text-sm md:text-base text-gray-700 mb-6 text-left">
                  {t("intakeProcess.session.wellnessPlanning.description")}
                </p>

                <div className="mb-6 text-left">
                  <h3 className="font-medium text-gray-900 mb-2">{t("intakeProcess.session.wellnessPlanning.focusAreas")}</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-600 pl-2 text-sm md:text-base">
                    <li>{t("intakeProcess.session.wellnessPlanning.areas.readinessAssessment")}</li>
                    <li>{t("intakeProcess.session.wellnessPlanning.areas.cognitivePattern")}</li>
                    <li>{t("intakeProcess.session.wellnessPlanning.areas.valueClarification")}</li>
                    <li>{t("intakeProcess.session.wellnessPlanning.areas.preliminaryGoalSetting")}</li>
                  </ul>
                </div>

                <div className="flex gap-2 md:gap-4 flex-wrap justify-center">
                  {!sessionStatus.session1.completed ? (
                    <LockedSessionButton stage={1}>
                      <Lock className="mr-2 h-4 w-4" />
                      {t("intakeProcess.buttons.completeSession1First")}
                    </LockedSessionButton>
                  ) : sessionStatus.session2.completed ? (
                    <CompletedSessionButton>
                      <Phone className="mr-2 h-4 w-4" />
                      {t("intakeProcess.buttons.talkToHana")}
                    </CompletedSessionButton>
                  ) : (
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="flex items-center text-sm md:text-base bg-primary text-white hover:bg-primary/90">
                          <Phone className="mr-2 h-4 w-4" />
                          {t("intakeProcess.buttons.talkToHana")}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                          <DialogTitle className="text-xl">{t("intakeProcess.session.wellnessPlanning.dialogTitle")}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <p className="text-sm text-gray-600">{t("intakeProcess.session.wellnessPlanning.dialogDescription")}</p>
                          
                          <div className="bg-gray-50 p-4 rounded-md">
                            <h3 className="font-medium text-gray-900 mb-2">{t("intakeProcess.session.wellnessPlanning.focusAreas")}</h3>
                            <ul className="list-disc list-inside space-y-1 text-gray-600 pl-2 text-sm">
                              <li>{t("intakeProcess.session.wellnessPlanning.areas.readinessAssessment")}</li>
                              <li>{t("intakeProcess.session.wellnessPlanning.areas.cognitivePattern")}</li>
                              <li>{t("intakeProcess.session.wellnessPlanning.areas.valueClarification")}</li>
                              <li>{t("intakeProcess.session.wellnessPlanning.areas.preliminaryGoalSetting")}</li>
                            </ul>
                          </div>
                          
                          <div className="flex justify-center pt-4">
                            <Button onClick={() => handleStartCall(2)} className="w-full sm:w-auto">
                              {t("intakeProcess.buttons.startSession")}
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  )}
                  
                  {!sessionStatus.session1.completed ? (
                    <Button variant="outline" className="flex items-center text-sm md:text-base bg-gray-100 text-gray-500 cursor-not-allowed" disabled>
                      <Lock className="mr-2 h-4 w-4" />
                      {t("intakeProcess.buttons.reportLocked")}
                    </Button>
                  ) : reportIds.INTAKE2 ? (
                    <Link to={reportIds.INTAKE2 ? `/auth/report/${reportIds.INTAKE2}/intake2` : `/auth/start-report/intake2`}>
                      <Button variant="outline" className="flex items-center text-sm md:text-base bg-[#EBF4F0] border-[#EBF4F0] hover:bg-[#EBF4F0]/90 hover:border-[#EBF4F0]/90 text-gray-700">
                        <FileText className="mr-2 h-4 w-4" />
                        {t("intakeProcess.buttons.viewReport")}
                      </Button>
                    </Link>
                  ) : (
                    <Button variant="outline" className="flex items-center text-sm md:text-base bg-gray-100 text-gray-500 cursor-not-allowed" disabled>
                      <FileText className="mr-2 h-4 w-4" />
                      {t("intakeProcess.buttons.reportUnavailable")}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Dividing Line */}
            <div className="w-1 h-12 bg-gray-200 mx-auto"></div>

            {/* Action Plan Development Card */}
            <Card id="action-plan" className={`border border-gray-200 shadow-sm ${!sessionStatus.session2.completed ? 'opacity-70' : ''}`}>
              <CardContent className={`${isMobile ? 'p-4' : 'p-6'}`}>
                <div className="flex justify-between items-start mb-4 flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white font-medium">
                      3
                    </div>
                    <h2 className="text-lg md:text-xl font-semibold">{t("intakeProcess.session.actionPlanDevelopment.title")}</h2>
                  </div>
                  <SchedulePill
                    status={sessionStatus.session3.completed ? 'completed' : (sessionStatus.session3.scheduled ? 'scheduled' : 'not-scheduled')}
                    appointment={callsByType.INTAKE3 || (reportIds.INTAKE3 ? {
                      id: reportIds.INTAKE3,
                      scheduledAt: reportDates?.INTAKE3 || new Date().toISOString(),
                      status: 'COMPLETED' as const,
                      type: 'INTAKE3' as const,
                      reportId: reportIds.INTAKE3,
                      createdAt: reportDates?.INTAKE3
                    } : {
                      type: 'INTAKE3' as const,
                      status: 'PENDING' as const
                    })}
                    date={sessionStatus.session3.date}
                    time={sessionStatus.session3.date ? sessionStatus.session3.date.split(' ')[1] + ' ' + sessionStatus.session3.date.split(' ')[2] : ''}
                    completedDate={sessionStatus.session3.completed ? sessionStatus.session3.date : undefined}
                    onReschedule={(date, time) => handleReschedule('session3', date, time)}
                    onSuccess={(updatedAppointment) => {
                      handleReschedule('session3', '', '');
                      refetchCalls();
                    }}
                    reportDate={reportIds.INTAKE3 && reportDates?.INTAKE3 ? format(new Date(reportDates.INTAKE3), 'MMM d, yyyy') : undefined}
                    callDate={callsByType.INTAKE3 && callsByType.INTAKE3.scheduledAt ? format(new Date(callsByType.INTAKE3.scheduledAt), 'MMM d, yyyy') : undefined}
                    hasReport={!!reportIds.INTAKE3}
                  />
                </div>

                <p className="text-sm md:text-base text-gray-700 mb-6 text-left">
                  {t("intakeProcess.session.actionPlanDevelopment.description")}
                </p>

                <div className="mb-6 text-left">
                  <h3 className="font-medium text-gray-900 mb-2">{t("intakeProcess.session.actionPlanDevelopment.focusAreas")}</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-600 pl-2 text-sm md:text-base">
                    <li>{t("intakeProcess.session.actionPlanDevelopment.areas.smartGoalFinalization")}</li>
                    <li>{t("intakeProcess.session.actionPlanDevelopment.areas.barrierIdentification")}</li>
                    <li>{t("intakeProcess.session.actionPlanDevelopment.areas.resourceConnection")}</li>
                    <li>{t("intakeProcess.session.actionPlanDevelopment.areas.progressMonitoring")}</li>
                  </ul>
                </div>

                <div className="flex gap-2 md:gap-4 flex-wrap justify-center">
                  {!sessionStatus.session2.completed ? (
                    <LockedSessionButton stage={2}>
                      <Lock className="mr-2 h-4 w-4" />
                      {t("intakeProcess.buttons.completeSession2First")}
                    </LockedSessionButton>
                  ) : sessionStatus.session3.completed ? (
                    <CompletedSessionButton>
                      <Phone className="mr-2 h-4 w-4" />
                      {t("intakeProcess.buttons.talkToHana")}
                    </CompletedSessionButton>
                  ) : (
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="flex items-center text-sm md:text-base bg-primary text-white hover:bg-primary/90">
                          <Phone className="mr-2 h-4 w-4" />
                          {t("intakeProcess.buttons.talkToHana")}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                          <DialogTitle className="text-xl">{t("intakeProcess.session.actionPlanDevelopment.dialogTitle")}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <p className="text-sm text-gray-600">{t("intakeProcess.session.actionPlanDevelopment.dialogDescription")}</p>
                          
                          <div className="bg-gray-50 p-4 rounded-md">
                            <h3 className="font-medium text-gray-900 mb-2">{t("intakeProcess.session.actionPlanDevelopment.focusAreas")}</h3>
                            <ul className="list-disc list-inside space-y-1 text-gray-600 pl-2 text-sm">
                              <li>{t("intakeProcess.session.actionPlanDevelopment.areas.smartGoalFinalization")}</li>
                              <li>{t("intakeProcess.session.actionPlanDevelopment.areas.barrierIdentification")}</li>
                              <li>{t("intakeProcess.session.actionPlanDevelopment.areas.resourceConnection")}</li>
                              <li>{t("intakeProcess.session.actionPlanDevelopment.areas.progressMonitoring")}</li>
                            </ul>
                          </div>
                          
                          <div className="flex justify-center pt-4">
                            <Button onClick={() => handleStartCall(3)} className="w-full sm:w-auto">
                              {t("intakeProcess.buttons.startSession")}
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  )}
                  
                  {!sessionStatus.session2.completed ? (
                    <Button variant="outline" className="flex items-center text-sm md:text-base bg-gray-100 text-gray-500 cursor-not-allowed" disabled>
                      <Lock className="mr-2 h-4 w-4" />
                      {t("intakeProcess.buttons.reportLocked")}
                    </Button>
                  ) : reportIds.INTAKE3 ? (
                    <Link to={reportIds.INTAKE3 ? `/auth/report/${reportIds.INTAKE3}/intake3` : `/auth/start-report/intake3`}>
                      <Button variant="outline" className="flex items-center text-sm md:text-base bg-[#EBF4F0] border-[#EBF4F0] hover:bg-[#EBF4F0]/90 hover:border-[#EBF4F0]/90 text-gray-700">
                        <FileText className="mr-2 h-4 w-4" />
                        {t("intakeProcess.buttons.viewReport")}
                      </Button>
                    </Link>
                  ) : (
                    <Button variant="outline" className="flex items-center text-sm md:text-base bg-gray-100 text-gray-500 cursor-not-allowed" disabled>
                      <FileText className="mr-2 h-4 w-4" />
                      {t("intakeProcess.buttons.reportUnavailable")}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      {showToast && <Toast message={toastMessage.message} variant={toastMessage.variant} setShowToast={setShowToast} />}
    </div>
  );
};

export default IntakeProcess;
